import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import UniversityKnownFlow from './flows/UniversityKnownFlow';
import FieldKnownFlow from './flows/FieldKnownFlow';
import UnsureFlow from './flows/UnsureFlow';

const AIOnboardingWizard = ({ path, onBackToEntry }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState({});

  // Define flows for each path
  const flows = {
    university: [
      {
        id: 'university_name',
        type: 'text',
        question: 'What university are you interested in?',
        placeholder: 'e.g., University of Oxford, MIT, Stanford',
        description: 'Enter the name of the university you\'re considering',
      },
      {
        id: 'degree_level',
        type: 'select',
        question: 'What degree level are you targeting?',
        options: ['Bachelor\'s', 'Master\'s', 'PhD', 'Diploma', 'Certificate Program'],
        description: 'This helps us provide relevant information',
      },
      {
        id: 'field_of_study',
        type: 'text',
        question: 'What field are you interested in studying?',
        placeholder: 'e.g., Computer Science, Medicine, Business Administration',
        description: 'Tell us your area of interest',
      },
      {
        id: 'specific_program',
        type: 'text',
        question: 'Do you have a specific program in mind?',
        placeholder: 'e.g., MSc in Artificial Intelligence, MBA, Biomedical Engineering',
        description: 'Optional - Leave blank if unsure',
      },
      {
        id: 'current_education',
        type: 'select',
        question: 'What is your current education level?',
        options: [
          'High School Student',
          'High School Graduate',
          'Bachelor\'s Student',
          'Bachelor\'s Graduate',
          'Master\'s Student',
          'Master\'s Graduate',
          'Working Professional',
        ],
        description: 'Helps us understand your academic background',
      },
      {
        id: 'academic_performance',
        type: 'select',
        question: 'How would you describe your academic performance?',
        options: [
          'Excellent (Top 10%)',
          'Very Good (Top 25%)',
          'Good (Top 50%)',
          'Average',
          'Prefer not to say',
        ],
        description: 'This helps us assess admission chances',
      },
      {
        id: 'gpa_score',
        type: 'text',
        question: 'What is your GPA or equivalent score?',
        placeholder: 'e.g., 3.8/4.0, 85%, First Class',
        description: 'Optional - Your current or most recent academic score',
      },
      {
        id: 'entrance_exams',
        type: 'select',
        question: 'Have you taken or plan to take standardized tests?',
        options: [
          'SAT/ACT (taken)',
          'SAT/ACT (planning)',
          'GRE (taken)',
          'GRE (planning)',
          'GMAT (taken)',
          'GMAT (planning)',
          'IELTS/TOEFL (taken)',
          'IELTS/TOEFL (planning)',
          'Not required/Not planning',
        ],
        description: 'Select all that apply to your application',
      },
      {
        id: 'why_this_university',
        type: 'select',
        question: 'What attracts you most to this university?',
        options: [
          'Academic reputation',
          'Specific programs',
          'Research opportunities',
          'Location',
          'Career prospects',
          'Campus culture',
          'Alumni network',
          'Financial aid',
        ],
        description: 'This helps us highlight relevant aspects',
      },
      {
        id: 'application_timeline',
        type: 'select',
        question: 'When do you plan to apply?',
        options: [
          'This semester',
          'Next semester',
          'This year',
          'Next year',
          'In 2+ years',
          'Just researching',
        ],
        description: 'We\'ll provide timeline-appropriate guidance',
      },
      {
        id: 'funding_needs',
        type: 'select',
        question: 'What are your funding requirements?',
        options: [
          'Need full scholarship',
          'Need partial scholarship',
          'Interested in student loans',
          'Self-funded',
          'Employer-sponsored',
          'Not sure yet',
        ],
        description: 'Helps us show relevant financial aid options',
      },
      {
        id: 'career_goals',
        type: 'text',
        question: 'What are your career goals after graduation?',
        placeholder: 'e.g., Software Engineer at tech company, Medical Researcher, Start own business',
        description: 'This helps us align recommendations with your aspirations',
      },
    ],
    field: [
      {
        id: 'field_of_interest',
        type: 'select',
        question: 'What field are you interested in?',
        options: [
          'Computer Science & Tech',
          'Engineering (Mechanical, Electrical, etc.)',
          'Business & Economics',
          'Medicine & Health Sciences',
          'Law & Legal Studies',
          'Arts & Humanities',
          'Natural Sciences (Physics, Chemistry, Biology)',
          'Social Sciences (Psychology, Sociology)',
          'Mathematics & Statistics',
          'Architecture & Design',
          'Education',
          'Environmental Studies',
        ],
        description: 'Choose your primary area of interest',
      },
      {
        id: 'specific_specialization',
        type: 'text',
        question: 'Any specific specialization within this field?',
        placeholder: 'e.g., Machine Learning, Cardiology, International Business',
        description: 'Optional - Be as specific as you like',
      },
      {
        id: 'degree_level',
        type: 'select',
        question: 'What degree level are you targeting?',
        options: ['Bachelor\'s', 'Master\'s', 'PhD', 'Professional Degree', 'Not sure yet'],
        description: 'This helps narrow down suitable programs',
      },
      {
        id: 'preferred_regions',
        type: 'select',
        question: 'Which region interests you most?',
        options: [
          'United States',
          'United Kingdom',
          'Western Europe',
          'Eastern Europe',
          'Canada',
          'Australia/New Zealand',
          'Asia (East Asia)',
          'Asia (South/Southeast Asia)',
          'Middle East',
          'No preference',
        ],
        description: 'We\'ll find universities in your preferred region',
      },
      {
        id: 'budget_range',
        type: 'select',
        question: 'What\'s your total budget per year (tuition + living)?',
        options: [
          'Under $10,000/year',
          '$10,000 - $25,000/year',
          '$25,000 - $50,000/year',
          '$50,000 - $75,000/year',
          'Over $75,000/year',
          'Budget flexible/Seeking scholarships',
        ],
        description: 'This helps us find universities that fit your budget',
      },
      {
        id: 'scholarship_requirement',
        type: 'select',
        question: 'How important is scholarship/financial aid?',
        options: [
          'Essential - Need 100% funding',
          'Very important - Need 50%+ funding',
          'Helpful but not required',
          'Not needed',
        ],
        description: 'We\'ll prioritize universities with strong aid programs',
      },
      {
        id: 'language_proficiency',
        type: 'select',
        question: 'What is your English proficiency level?',
        options: [
          'Native speaker',
          'Fluent (C1-C2)',
          'Advanced (B2)',
          'Intermediate (B1)',
          'Need to improve',
          'Plan to take English program first',
        ],
        description: 'Some programs may require language tests',
      },
      {
        id: 'study_mode',
        type: 'select',
        question: 'What study mode do you prefer?',
        options: [
          'Full-time on-campus',
          'Part-time on-campus',
          'Online/Distance learning',
          'Hybrid (mix of online and on-campus)',
          'No preference',
        ],
        description: 'This affects university and program options',
      },
      {
        id: 'university_size',
        type: 'select',
        question: 'Do you prefer a specific university size?',
        options: [
          'Large (15,000+ students)',
          'Medium (5,000-15,000 students)',
          'Small (Under 5,000 students)',
          'No preference',
        ],
        description: 'Class sizes and campus feel vary with university size',
      },
      {
        id: 'location_type',
        type: 'select',
        question: 'What type of location do you prefer?',
        options: [
          'Major city (urban)',
          'Mid-size city (suburban)',
          'College town (small city)',
          'Rural/Countryside',
          'No preference',
        ],
        description: 'Location affects lifestyle and opportunities',
      },
      {
        id: 'priority_factors',
        type: 'select',
        question: 'What matters most to you in a university?',
        options: [
          'Academic reputation/rankings',
          'Research opportunities',
          'Industry connections/internships',
          'Campus facilities',
          'Student support services',
          'Diversity and international community',
          'Affordability',
          'Post-graduation employment rate',
        ],
        description: 'Helps us personalize recommendations',
      },
      {
        id: 'extracurriculars',
        type: 'text',
        question: 'What are your key extracurricular activities or achievements?',
        placeholder: 'e.g., Student council, volunteering, sports, competitions, projects',
        description: 'Optional - This strengthens your profile',
      },
    ],
    unsure: [
      {
        id: 'career_interest',
        type: 'buttons',
        question: 'What excites you the most?',
        options: [
          { label: '💻 Technology & Innovation', value: 'tech' },
          { label: '🏥 Health & Medicine', value: 'health' },
          { label: '🏢 Business & Finance', value: 'business' },
          { label: '🔬 Research & Science', value: 'science' },
          { label: '✍️ Arts & Culture', value: 'arts' },
          { label: '🌍 Social Impact', value: 'social' },
        ],
        description: 'Select the area that resonates most with you',
      },
      {
        id: 'strengths',
        type: 'select',
        question: 'What are you naturally good at?',
        options: [
          'Problem-solving and logic',
          'Creative thinking',
          'Working with people',
          'Analysis and research',
          'Communication and writing',
          'Hands-on practical work',
          'Leadership and organization',
        ],
        description: 'Understanding your strengths helps guide recommendations',
      },
      {
        id: 'interests_hobbies',
        type: 'text',
        question: 'What are your main hobbies or interests?',
        placeholder: 'e.g., coding, reading, sports, music, volunteering, traveling',
        description: 'Your interests can point to suitable fields of study',
      },
      {
        id: 'favorite_subjects',
        type: 'text',
        question: 'What were your favorite subjects in school?',
        placeholder: 'e.g., Mathematics, History, Biology, Literature',
        description: 'This helps identify fields you might enjoy',
      },
      {
        id: 'career_vision',
        type: 'select',
        question: 'What kind of career do you envision?',
        options: [
          'Working for a big corporation',
          'Starting my own business',
          'Research and academia',
          'Non-profit/social sector',
          'Government/public service',
          'Freelance/creative work',
          'Healthcare professional',
          'Not sure yet',
        ],
        description: 'This helps align university programs with career paths',
      },
      {
        id: 'work_environment',
        type: 'select',
        question: 'What work environment appeals to you?',
        options: [
          'Office/corporate setting',
          'Laboratory/research facility',
          'Hospital/clinical setting',
          'Outdoors/field work',
          'Remote/work from anywhere',
          'Studio/creative space',
          'Mix of different environments',
        ],
        description: 'Different fields offer different work settings',
      },
      {
        id: 'study_style',
        type: 'buttons',
        question: 'How do you prefer to study?',
        options: [
          { label: '🏙️ Big Campus', value: 'big' },
          { label: '🏘️ Small University', value: 'small' },
          { label: '🌍 International', value: 'international' },
          { label: '💼 Industry-Focused', value: 'industry' },
          { label: '🎓 Research-Oriented', value: 'research' },
          { label: '🎨 Creative & Flexible', value: 'creative' },
        ],
        description: 'University culture varies widely',
      },
      {
        id: 'learning_preference',
        type: 'select',
        question: 'How do you learn best?',
        options: [
          'Hands-on practical experience',
          'Theoretical and conceptual',
          'Mix of theory and practice',
          'Group projects and collaboration',
          'Independent study',
          'Visual and creative methods',
        ],
        description: 'Different programs emphasize different learning styles',
      },
      {
        id: 'education_level',
        type: 'select',
        question: 'What is your current education level?',
        options: [
          'High School Student',
          'High School Graduate',
          'Some College/University',
          'Bachelor\'s Degree',
          'Working Professional',
          'Career Change',
        ],
        description: 'Helps us suggest appropriate degree levels',
      },
      {
        id: 'budget_awareness',
        type: 'select',
        question: 'What\'s your budget situation?',
        options: [
          'Very limited - Need full scholarship',
          'Moderate - Need partial funding',
          'Flexible - Can afford most programs',
          'Not a constraint',
          'Haven\'t thought about it yet',
        ],
        description: 'Budget affects which universities we recommend',
      },
      {
        id: 'location_openness',
        type: 'select',
        question: 'How do you feel about studying abroad?',
        options: [
          'Very excited - Prefer international',
          'Open to it',
          'Prefer staying in home country',
          'Only domestic options',
          'Undecided',
        ],
        description: 'Opens up different opportunities',
      },
      {
        id: 'help_needed',
        type: 'select',
        question: 'What type of help would you find most useful?',
        options: [
          'Discovering what to study',
          'Finding the right universities',
          'Understanding admission requirements',
          'Scholarship and funding guidance',
          'Application process support',
          'Career path exploration',
          'All of the above',
        ],
        description: 'We\'ll tailor our guidance to your needs',
      },
    ],
  };

  const flowSteps = flows[path];
  const totalSteps = flowSteps.length;
  const currentQuestion = flowSteps[currentStep];
  const progressPercent = ((currentStep + 1) / totalSteps) * 100;

  const MULTI_FIELDS = new Set(['preferred_regions', 'priority_factors', 'field_of_interest']);

  const handleNext = (answer) => {
    setResponses({
      ...responses,
      [currentQuestion.id]: answer,
    });

    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Last step - show summary
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    console.log('Onboarding complete with responses:', {
      path,
      responses,
    });
    // Here you would typically send data to backend
    alert('Thank you! Your responses have been recorded. (Mock completion)');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-gray-950 flex flex-col items-center justify-center px-4 py-12">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/2 w-96 h-96 bg-primary/20 rounded-full blur-3xl opacity-30 transform -translate-x-1/2"></div>
      </div>

      <div className="relative z-10 w-full max-w-2xl">
        {/* Header with back button */}
        <div className="flex items-center justify-between mb-12">
          <button
            onClick={onBackToEntry}
            className="flex items-center text-gray-400 hover:text-white transition-colors duration-300 group"
          >
            <ChevronLeft size={20} className="mr-1 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm">Back</span>
          </button>
          <div className="text-gray-500 text-sm">
            Step {currentStep + 1} of {totalSteps}
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-8">
          <div className="w-full h-1 bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary to-primary transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
        </div>

        {/* Question Container */}
        <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-700 rounded-2xl p-8 md:p-12 shadow-2xl">
          <div className="mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
              {currentQuestion.question}
            </h2>
            {currentQuestion.description && (
              <p className="text-gray-400 text-lg">
                {currentQuestion.description}
              </p>
            )}
          </div>

          {/* Question content based on type */}
          <div className="mb-8">
            {currentQuestion.type === 'text' && (
              <input
                type="text"
                placeholder={currentQuestion.placeholder}
                defaultValue={responses[currentQuestion.id] || ''}
                onChange={(e) => {
                  setResponses({
                    ...responses,
                    [currentQuestion.id]: e.target.value,
                  });
                }}
                className="w-full px-6 py-4 bg-background border-2 border-input rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300 text-lg"
              />
            )}

            {currentQuestion.type === 'select' && (
              <div>
                {MULTI_FIELDS.has(currentQuestion.id) && (
                  <div className="flex justify-end mb-3">
                    <span className="text-sm text-gray-400">
                      Selected: {Array.isArray(responses[currentQuestion.id]) ? responses[currentQuestion.id].length : 0}
                    </span>
                  </div>
                )}
                <div className={`${MULTI_FIELDS.has(currentQuestion.id) ? 'grid grid-cols-1 md:grid-cols-2 gap-3' : 'space-y-3'}`}>
                {currentQuestion.options.map((option) => {
                  const isMulti = MULTI_FIELDS.has(currentQuestion.id);
                  const currentValue = responses[currentQuestion.id];
                  const isSelected = isMulti
                    ? Array.isArray(currentValue) && currentValue.includes(option)
                    : currentValue === option;

                  const handleSelect = () => {
                    if (!isMulti) {
                      setResponses({
                        ...responses,
                        [currentQuestion.id]: option,
                      });
                      return;
                    }
                    const existing = Array.isArray(currentValue) ? currentValue : [];
                    const next = isSelected
                      ? existing.filter((o) => o !== option)
                      : [...existing, option];
                    setResponses({
                      ...responses,
                      [currentQuestion.id]: next,
                    });
                  };

                  if (isMulti) {
                    return (
                      <label
                        key={option}
                        className={`group flex flex-col items-center gap-3 w-full px-6 py-6 rounded-xl border-2 transition-all duration-300 text-center font-medium ${
                          isSelected
                            ? 'border-primary bg-gray-100 dark:bg-gray-800 text-foreground ring-2 ring-primary/30'
                            : 'border-gray-300 dark:border-gray-700 bg-background text-foreground hover:border-primary/50 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                        } hover:shadow-md`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={handleSelect}
                          className="h-6 w-6 accent-amber-500 border-gray-700 rounded-md"
                          aria-label={option}
                        />
                        <span className="whitespace-normal break-words leading-snug">{option}</span>
                      </label>
                    );
                  }

                  return (
                    <button
                      key={option}
                      onClick={handleSelect}
                      className={`w-full px-6 py-4 rounded-xl border-2 transition-all duration-300 text-left font-medium ${
                        isSelected
                          ? 'border-primary bg-gray-100 dark:bg-gray-800 text-foreground'
                          : 'border-gray-300 dark:border-gray-700 bg-background text-foreground hover:border-primary/50 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                      }`}
                    >
                      {option}
                    </button>
                  );
                })}
                </div>
              </div>
            )}

            {currentQuestion.type === 'buttons' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {currentQuestion.options.map((option) => (
                  <button
                    key={option.value}
                    onClick={() =>
                      setResponses({
                        ...responses,
                        [currentQuestion.id]: option.value,
                      })
                    }
                    className={`px-6 py-6 rounded-xl border-2 transition-all duration-300 font-medium text-center group ${
                      responses[currentQuestion.id] === option.value
                        ? 'border-primary bg-gray-100 dark:bg-gray-800 text-foreground'
                        : 'border-gray-300 dark:border-gray-700 bg-background text-foreground hover:border-primary/50 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                    }`}
                  >
                    <div className="text-2xl mb-2">{option.label.split(' ')[0]}</div>
                    <div className="text-sm">{option.label.split(' ').slice(1).join(' ')}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Navigation buttons */}
          <div className="flex gap-4 pt-8 border-t border-gray-700">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className="flex-1 px-6 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2"
            >
              <ChevronLeft size={18} />
              <span>Back</span>
            </button>
            <button
              onClick={() => handleNext(responses[currentQuestion.id])}
              disabled={(() => {
                const val = responses[currentQuestion.id];
                if (MULTI_FIELDS.has(currentQuestion.id)) {
                  return !Array.isArray(val) || val.length === 0;
                }
                return !val;
              })()}
              className="flex-1 px-6 py-3 bg-primary rounded-xl text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2 group"
            >
              <span>{currentStep === totalSteps - 1 ? 'Complete' : 'Next'}</span>
              {currentStep < totalSteps - 1 && (
                <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
              )}
            </button>
          </div>
        </div>

        {/* Help text */}
        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>Your responses help us personalize your university recommendations</p>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default AIOnboardingWizard;
